<template>
  <template
    v-if="
      timesheetFormStore.isViewOnly &&
      !timesheetFormStore.isDateDisabled(col.dateString) &&
      timesheetFormStore.tableDateEntries[col.dateString].startTime &&
      timesheetFormStore.tableDateEntries[col.dateString].endTime
    "
  >
    <div class="tw-text-right tw-p-sm">
      {{ timesheetFormStore.tableDateEntries[col.dateString][row.entriesKey] }}
      {{
        timesheetFormStore.useDayInputs ? $t('d (day abbreviation)') : $t('h (hour abbreviation)')
      }}
    </div>
  </template>
  <WdsInput
    v-if="!timesheetFormStore.isViewOnly"
    v-model="timesheetFormStore.tableDateEntries[col.dateString][row.entriesKey]"
    :data-test="`${col.dateString}-${row.entriesKey}`"
    class="tw-w-full"
    type="number"
    step="0.25"
    min="0"
    :disabled="timesheetFormStore.isDateDisabled(col.dateString)"
    :error="getErrorMessage(col.dateString, row.entriesKey)"
    @keydown="handleKeydown"
    @focus="handleInputFocused"
    @update:model-value="handleInputChanged"
  />
</template>

<script setup lang="ts">
import { computed, nextTick } from 'vue'
import { WdsInput } from '@worksome/wds-components'
import { useTranslation } from '@/composables/translation'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import { useValidationStore } from '@/components/Timesheet/store/useValidationStore'
import formatFixedDecimal from '@/components/Timesheet/utilities/formatFixedDecimal'
import { Message } from '@/utils/notification'
import { useTimesheetLinesStore } from '@/components/Timesheet/store/useTimesheetLinesStore'

interface Props {
  row: any
  col: any
}

const props = defineProps<Props>()

const { $t } = useTranslation()
const timesheetFormStore = useTimesheetFormStore()
const timesheetValidationStore = useValidationStore()
const timesheetLinesStore = useTimesheetLinesStore()

const getErrorMessage = (columnDate, entryKey) =>
  timesheetValidationStore.v$[columnDate]?.[entryKey]?.$errors[0]?.$message

const handleKeydown = (event) => {
  if (event.key === '-') {
    event.preventDefault()
  }
}

const handleInputFocused = async () => {
  if (timesheetFormStore.tableDateEntries[props.col.dateString][props.row.entriesKey] === 0) {
    await nextTick()
    timesheetFormStore.tableDateEntries[props.col.dateString][props.row.entriesKey] = null
  }
}

const maxDuration = computed(() => {
  const totalTimeReported = timesheetFormStore.sumOfTimePerDate[props.col.dateString] || 0
  const totalOfOtherTimesheetLines = timesheetLinesStore.sumOfTimesheetLines[props.col.dateString]

  return formatFixedDecimal(
    Math.max(
      0,
      totalTimeReported -
        totalOfOtherTimesheetLines +
        timesheetFormStore.tableDateEntries[props.col.dateString][props.row.entriesKey],
    ),
  )
})

const handleInputChanged = async (e) => {
  const newDuration = Number(e)
  const totalAvailableTimeToAssign = maxDuration.value
  if (newDuration > totalAvailableTimeToAssign) {
    new Message(
      timesheetLinesStore.sumOfTimesheetLines[props.col.dateString] > 0
        ? $t('Break time cannot exceed the daily total time worked plus timesheet lines.')
        : $t('Break time cannot exceed the daily total time worked.'),
    ).dispatch()
    await nextTick()
    timesheetFormStore.tableDateEntries[props.col.dateString][props.row.entriesKey] =
      formatFixedDecimal(totalAvailableTimeToAssign)
  }
}
</script>

<style scoped>
@tailwind base;

:deep(.wds-error) {
  @apply !tw-w-20;
  @apply !tw-min-w-full;
}
</style>
